import type { Dispatch } from "redux";
import moment from "moment";
import { SocialPostStatus } from "../config";
import { SOCIAL_POST_TYPES } from "../consts/socialPostType.constant";
import { Media, Post, SocialPost } from "../interfaces/api/get-social-posts";
import { addOptimisticScheduledPostAction } from "../store/api-actions";

type LooseMedia = {
  key?: unknown;
  url?: unknown;
  type?: unknown;
  socialPostId?: unknown;
};
type LooseSocialPost = {
  id?: unknown;
  socialPostId?: unknown;
  socialProvider?: unknown;
  text?: unknown;
  companyId?: unknown;
  postAt?: unknown;
  type?: unknown;
  scheduleDate?: unknown;
  target?: unknown;
  status?: unknown;
  attempt?: unknown;
  error?: unknown;
  publisherAvatarUrl?: unknown;
  publisherName?: unknown;
  errorCode?: unknown;
};

/** Fila provisional en historial; se quita cuando el listado incluye el mismo caso (huella texto+fecha+red). */
export const PENDING_SCHEDULE_ID_PREFIX = "pending-schedule-";

function asRecord(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== "object") return null;
  return v as Record<string, unknown>;
}

/** Soporta varias formas de payload que devuelve el backend al programar. */
function extractSchedulePayload(apiResponse: unknown): {
  socialPost?: LooseSocialPost;
  media?: LooseMedia[];
} | null {
  const root = asRecord(apiResponse);
  if (!root) return null;

  const tryShape = (
    blob: Record<string, unknown> | null
  ): {
    socialPost?: LooseSocialPost;
    media?: LooseMedia[];
  } | null => {
    if (!blob) return null;
    const sp = blob.socialPost;
    const media = blob.media;
    if (sp && typeof sp === "object")
      return {
        socialPost: sp as LooseSocialPost,
        media: Array.isArray(media) ? (media as LooseMedia[]) : [],
      };
    return null;
  };

  const p = root.payload;
  let fromPayload = tryShape(asRecord(p));
  if (fromPayload) return fromPayload;

  const data = asRecord(p)?.data;
  fromPayload = tryShape(asRecord(data));
  if (fromPayload) return fromPayload;

  fromPayload = tryShape(root);
  if (fromPayload) return fromPayload;

  return null;
}

function pickId(sp: LooseSocialPost, mediaRaw: LooseMedia[]): string | null {
  const candidates = [
    sp.id,
    sp.socialPostId,
    mediaRaw[0]?.socialPostId,
  ];
  for (const c of candidates) {
    if (c === null || c === undefined) continue;
    const s = String(c).trim();
    if (s !== "") return s;
  }
  return null;
}

function inferType(
  postType: "video" | "image" | "text",
  fromApi: unknown
): string {
  if (fromApi != null && String(fromApi).trim() !== "") return String(fromApi);
  if (postType === "video") return SOCIAL_POST_TYPES.VIDEO;
  if (postType === "image") return SOCIAL_POST_TYPES.IMAGE;
  return "text";
}

/**
 * Construye un ítem de historial/calendario a partir de la respuesta al programar,
 * para mostrarlo antes de que GET /social/posts lo devuelva (p. ej. vídeo en cola).
 */
export function socialPostFromScheduleApiResponse(
  apiResponse: unknown,
  ctx: {
    scheduleDate: string;
    companyId: string;
    defaultProvider: string;
    postType: "video" | "image" | "text";
  }
): SocialPost | null {
  const extracted = extractSchedulePayload(apiResponse);
  const sp = extracted?.socialPost;
  if (!sp || typeof sp !== "object") return null;

  const mediaRaw =
    extracted?.media && Array.isArray(extracted.media)
      ? extracted.media
      : [];
  const id = pickId(sp, mediaRaw);
  if (!id) return null;

  const post: Post = {
    id,
    socialProvider: String(sp.socialProvider ?? ctx.defaultProvider),
    text: String(sp.text ?? ""),
    companyId: Number(sp.companyId ?? ctx.companyId),
    postAt: sp.postAt != null ? String(sp.postAt) : "",
    type: inferType(ctx.postType, sp.type),
    scheduleDate:
      sp.scheduleDate != null && String(sp.scheduleDate).trim() !== ""
        ? String(sp.scheduleDate)
        : ctx.scheduleDate,
    target: String(sp.target ?? "post"),
    status: sp.status != null ? Number(sp.status) : SocialPostStatus.Waiting,
    attempt: sp.attempt != null ? Number(sp.attempt) : 0,
    error: sp.error != null ? String(sp.error) : "",
    publisherAvatarUrl:
      sp.publisherAvatarUrl != null ? String(sp.publisherAvatarUrl) : "",
    publisherName: sp.publisherName != null ? String(sp.publisherName) : "",
    errorCode: sp.errorCode != null ? Number(sp.errorCode) : 0,
  };

  const media: Media[] = mediaRaw.map((m) => ({
    key: m.key != null ? String(m.key) : "",
    url: m.url != null ? String(m.url) : "",
    type: m.type != null ? String(m.type) : null,
  }));

  return { post, media, mention: [] };
}

function normText(s: string): string {
  return s.trim().replace(/\s+/g, " ");
}

function normProv(raw: string): string {
  return String(raw ?? "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

/** Misma pieza ya listada por el servidor: quita la fila `pending-schedule-*`. */
export function pendingScheduledOptimisticSuperseded(
  pending: SocialPost,
  serverPosts: SocialPost[]
): boolean {
  if (!String(pending.post.id).startsWith(PENDING_SCHEDULE_ID_PREFIX))
    return false;
  const pt = normText(pending.post.text);
  const pp = normProv(pending.post.socialProvider);
  const pSched = pending.post.scheduleDate;
  if (!pSched || !pt) return false;

  const pMoment = moment(pSched);
  if (!pMoment.isValid()) return false;

  return serverPosts.some((s) => {
    const sp = s.post;
    if (!sp.scheduleDate || normText(sp.text) !== pt) return false;
    if (!moment(sp.scheduleDate).isValid()) return false;
    if (
      !moment(sp.scheduleDate).isSame(moment(pSched), "minute")
    )
      return false;
    const spProv = normProv(sp.socialProvider);
    return (
      spProv === pp ||
      spProv.endsWith(pp) ||
      pp.endsWith(spProv)
    );
  });
}

/** Si el API no devuelve id, mostramos de inmediato una fila “En espera” coherente con el compositor. */
export function buildPendingScheduledSocialPost(ctx: {
  scheduleDate: string;
  companyId: string;
  defaultProvider: string;
  postType: "video" | "image" | "text";
  text: string;
}): SocialPost {
  const id = `${PENDING_SCHEDULE_ID_PREFIX}${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 9)}`;

  const post: Post = {
    id,
    socialProvider: ctx.defaultProvider,
    text: ctx.text ?? "",
    companyId: Number(ctx.companyId),
    postAt: "",
    type: inferType(ctx.postType, undefined),
    scheduleDate: ctx.scheduleDate,
    target: "post",
    status: SocialPostStatus.Waiting,
    attempt: 0,
    error: "",
    publisherAvatarUrl: "",
    publisherName: "",
    errorCode: 0,
  };

  return { post, media: [], mention: [] };
}

export function dispatchOptimisticScheduledPostIfNeeded(
  dispatch: Dispatch,
  apiResponse: unknown,
  ctx: {
    scheduleDate: string;
    companyId: string;
    defaultProvider: string;
    postType: "video" | "image" | "text";
    text: string;
  }
) {
  if (!ctx.scheduleDate) return;
  const fromApi = socialPostFromScheduleApiResponse(apiResponse, ctx);
  const post =
    fromApi ?? buildPendingScheduledSocialPost(ctx);
  dispatch(addOptimisticScheduledPostAction({ post }));
}
